import type {
  NewsFeedQuery,
  NewsScope,
  NewsSector,
  NewsShow,
  NewsSort,
  NewsSource,
} from '@/schemas/news'

/**
 * The Market update / News filter model, shared by both tabs: what the Filters dialog edits, the
 * chips under the search row, and which of it mm-be serves.
 *
 * mm-be's `/market/updates/` takes `category` and `q` only. Every other control is drawn to the
 * mock and selectable, and selecting it lands on the no-data state rather than a request (owner
 * ruling 1, 2026-10-28; contract in `asks/nw1.md`). The defaults are the served options, so a
 * fresh page always shows data.
 */
export interface NewsFilterValues {
  scope: NewsScope
  show: NewsShow
  sectors: readonly NewsSector[]
  sources: readonly NewsSource[]
}

export const NEWS_FILTER_DEFAULTS: NewsFilterValues = {
  scope: 'all',
  show: 'everything',
  sectors: [],
  sources: [],
}

interface Option<T extends string> {
  value: T
  label: string
}

export const CATEGORY_OPTIONS: readonly Option<NewsScope>[] = [
  { value: 'all', label: 'All' },
  { value: 'companies', label: 'Companies' },
  { value: 'sectors', label: 'Sectors' },
  { value: 'brokers', label: 'Brokers' },
]

export const SHOW_OPTIONS: readonly Option<NewsShow>[] = [
  { value: 'holdings', label: 'My holdings' },
  { value: 'watchlist', label: 'My watchlist' },
  { value: 'everything', label: 'Everything' },
]

export const SECTOR_OPTIONS: readonly Option<NewsSector>[] = [
  { value: 'commercial-banks', label: 'Commercial banks' },
  { value: 'hydropower', label: 'Hydropower' },
  { value: 'microfinance', label: 'Microfinance' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'investment', label: 'Investment' },
  { value: 'hotels', label: 'Hotels' },
]

export const SOURCE_OPTIONS: readonly Option<NewsSource>[] = [
  { value: 'sarokar', label: 'Artha Sarokar' },
  { value: 'sansar', label: 'Artha Sansar' },
  { value: 'kendra', label: 'Artha Kendra' },
  { value: 'filing', label: 'Company filing' },
  { value: 'notice', label: 'NEPSE notice' },
]

export const SORT_OPTIONS: readonly Option<NewsSort>[] = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
]

const labelOf = <T extends string>(options: readonly Option<T>[], value: T): string =>
  options.find((option) => option.value === value)?.label ?? value

/** One removable chip under the search row; `id` is what `removeFilterChip` takes back. */
export interface FilterChip {
  id: string
  label: string
}

/** The applied filters as chips, in the mock's order: show, category, sectors, sources. */
export const filterChips = (filters: NewsFilterValues): FilterChip[] => [
  ...(filters.show === 'everything'
    ? []
    : [{ id: 'show', label: labelOf(SHOW_OPTIONS, filters.show) }]),
  ...(filters.scope === 'all'
    ? []
    : [{ id: 'scope', label: labelOf(CATEGORY_OPTIONS, filters.scope) }]),
  ...filters.sectors.map((sector) => ({
    id: `sector:${sector}`,
    label: labelOf(SECTOR_OPTIONS, sector),
  })),
  ...filters.sources.map((source) => ({
    id: `source:${source}`,
    label: labelOf(SOURCE_OPTIONS, source),
  })),
]

export const removeFilterChip = (filters: NewsFilterValues, id: string): NewsFilterValues => {
  if (id === 'show') return { ...filters, show: 'everything' }
  if (id === 'scope') return { ...filters, scope: 'all' }
  return {
    ...filters,
    sectors: filters.sectors.filter((sector) => `sector:${sector}` !== id),
    sources: filters.sources.filter((source) => `source:${source}` !== id),
  }
}

/**
 * The wire's `category` for a scope. mm-be matches it case-insensitively against the post's own
 * category, in the singular; the old `scope.slice(0, -1)` sent "companie".
 */
export const CATEGORY_PARAM: Record<Exclude<NewsScope, 'all'>, string> = {
  companies: 'company',
  sectors: 'sector',
  brokers: 'broker',
}

export const toggleIn = <T>(list: readonly T[], value: T): T[] =>
  list.includes(value) ? list.filter((item) => item !== value) : [...list, value]

/**
 * The feed query for these filters, each unserved group left out at its served value, so the
 * default view shares its cache entry with Home's "Today's news".
 */
export const toFeedQuery = (
  filters: NewsFilterValues,
  sort: NewsSort,
  query: string | undefined,
): NewsFeedQuery => ({
  scope: filters.scope,
  sort,
  ...(query === undefined ? {} : { query }),
  ...(filters.show === 'everything' ? {} : { show: filters.show }),
  ...(filters.sectors.length === 0 ? {} : { sectors: [...filters.sectors] }),
  ...(filters.sources.length === 0 ? {} : { sources: [...filters.sources] }),
})

/** True when the query asks only what `/market/updates/` serves: newest first, category, text. */
export const isServedFeedQuery = (query: NewsFeedQuery): boolean =>
  query.sort === 'newest' &&
  (query.show ?? 'everything') === 'everything' &&
  (query.sectors ?? []).length === 0 &&
  (query.sources ?? []).length === 0
